/**
 * LanguageContext (Solid.js)
 */

import { createContext, useContext, createSignal, createEffect, onMount } from 'solid-js';
import { useAuth } from './AuthContext';
import apiClient from '../utils/api';
import { storage } from '../utils/storage';

import deTranslations from '../i18n/translations/de.json';
import enTranslations from '../i18n/translations/en.json';
import frTranslations from '../i18n/translations/fr.json';
import esTranslations from '../i18n/translations/es.json';
import plTranslations from '../i18n/translations/pl.json';
import nlTranslations from '../i18n/translations/nl.json';
import itTranslations from '../i18n/translations/it.json';
import ptTranslations from '../i18n/translations/pt.json';

/** Angebotene Sprachen. Die übrigen Dateien sind kaum übersetzt und bleiben unerreichbar. */
const SUPPORTED_LANGUAGES = ['de', 'en'];
const sanitizeLanguage = (lang: string | null | undefined): string => (lang && SUPPORTED_LANGUAGES.includes(lang) ? lang : 'de');

const translations: Record<string, Record<string, unknown>> = {
  de: deTranslations as Record<string, unknown>,
  en: enTranslations as Record<string, unknown>,
  fr: frTranslations as Record<string, unknown>,
  es: esTranslations as Record<string, unknown>,
  pl: plTranslations as Record<string, unknown>,
  nl: nlTranslations as Record<string, unknown>,
  it: itTranslations as Record<string, unknown>,
  pt: ptTranslations as Record<string, unknown>,
};

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => Promise<void>;
  t: (key: string, defaultValueOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>) => string;
}

const LanguageContext = createContext<LanguageContextType>();

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage muss innerhalb von LanguageProvider verwendet werden');
  return ctx;
};

export const useTranslation = () => {
  const { language, t } = useLanguage();
  return { t, language };
};

export function LanguageProvider(props: { children: import('solid-js').JSX.Element }) {
  const auth = useAuth();
  const savedLanguage = sanitizeLanguage(storage.getItem('language'));
  const [language, setLanguageState] = createSignal<string>(savedLanguage);

  createEffect(() => {
    const uid = auth.user?.id;
    if (!uid) return;
    apiClient.get<{ success?: boolean; gameState?: { user?: { language?: string } } }>(`/game/state/${uid}`)
      .then((res) => {
        const lang = res.data?.gameState?.user?.language;
        if (lang) {
          const safe = sanitizeLanguage(lang);
          setLanguageState(safe);
          storage.setItem('language', safe);
        }
      })
      .catch(() => {});
  });

  const setLanguage = async (newLanguage: string): Promise<void> => {
    newLanguage = sanitizeLanguage(newLanguage);
    setLanguageState(newLanguage);
    storage.setItem('language', newLanguage);
    if (auth.user?.id) {
      try {
        await apiClient.put(`/user/${auth.user.id}`, { language: newLanguage });
        await auth.refreshUser();
      } catch {
        /* still lokal gesetzt */
      }
    }
  };

  const t = (key: string, defaultValueOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string => {
    let defaultValue: string | undefined;
    let translationParams: Record<string, unknown> = {};
    if (typeof defaultValueOrParams === 'string') {
      defaultValue = defaultValueOrParams;
      translationParams = params || {};
    } else if (defaultValueOrParams && typeof defaultValueOrParams === 'object') {
      translationParams = defaultValueOrParams;
    }
    const keys = key.split('.');
    // M18: Vorher nur Fallback auf de → jetzt Kette: aktive Sprache → en → de (Teilübersetzungen zeigen Englisch statt Deutsch)
    const lookup = (dict: Record<string, unknown> | undefined): unknown => {
      let v: unknown = dict;
      for (const k of keys) {
        if (v && typeof v === 'object' && k in (v as object)) {
          v = (v as Record<string, unknown>)[k];
        } else {
          return undefined;
        }
      }
      return v;
    };
    const lang = language();
    let value: unknown = lookup(translations[lang]);
    if (value === undefined && lang !== 'en') value = lookup(translations['en']);
    if (value === undefined && lang !== 'de') value = lookup(translations['de']);
    if (value === undefined) return (defaultValue ?? key) as string;
    if (typeof value === 'string' && Object.keys(translationParams).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (_, k) => (translationParams[k] !== undefined ? String(translationParams[k]) : _));
    }
    return (value as string) || (defaultValue ?? key);
  };

  // Keep <html lang="..."> in sync with the active language
  onMount(() => { document.documentElement.lang = language(); });
  createEffect(() => { document.documentElement.lang = language(); });

  const value: LanguageContextType = { get language() { return language(); }, setLanguage, t };

  return (
    <LanguageContext.Provider value={value}>
      {props.children}
    </LanguageContext.Provider>
  );
}
